import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Section, Container, SectionTitle } from '../styles/StyledComponents';

const CertificatesContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CertificatesIntro = styled(motion.div)`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  
  p {
    color: var(--medium-gray);
    line-height: 1.8;
    font-size: 1.1rem;
  }
`;

const CertificatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  width: 100%;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const CertificateCard = styled(motion.div)`
  background-color: var(--white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const CertificateIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--light-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 30px;
    height: 30px;
    color: var(--primary-blue);
  }
`;

const CertificateTitle = styled.h3`
  font-size: 1.3rem;
  color: var(--dark-blue);
  margin-bottom: 0.75rem;
`;

const CertificateIssuer = styled.h4`
  font-size: 1rem;
  color: var(--primary-blue);
  margin-bottom: 1rem;
`;

const CertificateDate = styled.p`
  color: var(--medium-gray);
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const CertificateDescription = styled.p`
  color: var(--medium-gray);
  font-size: 0.95rem;
  line-height: 1.6;
  flex-grow: 1;
`;

const Certificates: React.FC = () => {
  const certificatesData = [
    {
      title: "Certified Professional Coder (CPC) Training",
      issuer: "Udemy by Sunitha B.",
      date: "Completed: March 2025",
      description: "Specialized in outpatient billing workflows, coding audits, and reimbursement optimization."
    },
    {
      title: "Medical Billing & Coding Certification",
      issuer: "Udemy by Getonto Inc.",
      date: "Completed: March 2025",
      description: "Expertise in ICD-10, CPT, HCPCS, HIPAA compliance, and claims submission."
    },
    {
      title: "Introduction to Artificial Intelligence",
      issuer: "Coursera",
      date: "Completed: March 2025",
      description: "Gained foundational AI knowledge, including machine learning, neural networks, and healthcare analytics."
    },
    {
      title: "Certified Electronic Health Records Specialist (CEHRS)",
      issuer: "Udemy by Valerie Boyd-Holsinger",
      date: "Ongoing: May 2025",
      description: "Currently completing EHR training focused on HIPAA compliance, clinical documentation, and patient data management to support accurate and efficient healthcare administration."
    },
    {
      title: "HIPAA Compliance Complete Course",
      issuer: "Udemy by At My Desk Training AM",
      date: "Ongoing: May 2025",
      description: "Completed HIPAA Compliance training covering data privacy, security rules, and legal standards for protecting patient health information."
    }
  ];

  return (
    <Section id="certificates">
      <CertificatesContainer>
        <SectionTitle
          as={motion.h2}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Certifications & Training
        </SectionTitle>
        
        <CertificatesIntro
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <p>
            I am committed to continuous professional development and staying current with industry standards and best practices.
            My certifications reflect my dedication to excellence in healthcare administration, coding, and technology.
          </p>
        </CertificatesIntro>
        
        <CertificatesGrid>
          {certificatesData.map((cert, index) => (
            <CertificateCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <CertificateIcon>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </CertificateIcon>
              <CertificateTitle>{cert.title}</CertificateTitle>
              <CertificateIssuer>{cert.issuer}</CertificateIssuer>
              <CertificateDate>{cert.date}</CertificateDate>
              <CertificateDescription>{cert.description}</CertificateDescription>
            </CertificateCard>
          ))}
        </CertificatesGrid>
      </CertificatesContainer>
    </Section>
  );
};

export default Certificates;
